import type {
  AiKnownOperation,
  AiOperationalContinuitySummary,
  AiOperationSimulation,
  AiOperationalForecast,
  AiOperationalIntelligencePreview,
  AiOperationalRiskLevel,
} from '../../../types/aiOperational'

type SimulationIntent = {
  risk: AiOperationalRiskLevel
  nextStep: string
  explanation: string
}

const unique = (values: string[]) => values.filter((value, index, all) => value.trim() && all.indexOf(value) === index)

const rollbackAvailability = (operation: AiKnownOperation): AiOperationSimulation['rollbackAvailability'] => {
  if (operation.risk === 'LOW') return 'NOT_REQUIRED'
  if (operation.rollback?.reversible && operation.rollback.contract) return 'AVAILABLE'
  if (operation.rollback?.reversible) return 'PARTIAL'
  return 'UNAVAILABLE'
}

const governanceRequirements = (operation: AiKnownOperation, intent: SimulationIntent) => {
  const requirements: string[] = []
  if (operation.requiresConfirmation) requirements.push('Explicit user confirmation before proposal preparation.')
  if (operation.requiresReview) requirements.push('Human proposal review before trusted execution.')
  if (operation.kind === 'CHECKLIST_STATUS' || operation.kind === 'CHECKLIST_DEPENDENCY') {
    requirements.push('DeltaProcessor remains the authoritative mutation path.')
  }
  if (operation.risk === 'HIGH' || operation.risk === 'RESTRICTED') requirements.push('Audit trail must capture actor, source, and rollback references.')
  if (intent.risk === 'RESTRICTED') requirements.push('Restricted operation boundary blocks autonomous execution.')
  return requirements
}

const integrationTouchpoints = (operation: AiKnownOperation) => {
  const text = `${operation.description ?? ''} ${operation.title}`.toLowerCase()
  return unique([
    text.includes('upload') || text.includes('file') ? 'UploadHub' : '',
    text.includes('sync') ? 'provider sync' : '',
    text.includes('drive') || text.includes('notion') || text.includes('jira') || text.includes('calendar') ? 'Link Hub provider readiness' : '',
  ])
}

export const buildExecutionSimulation = ({
  operation,
  intent,
  intelligencePreview,
  forecasts,
  continuity,
}: {
  operation: AiKnownOperation
  intent: SimulationIntent
  intelligencePreview?: AiOperationalIntelligencePreview
  forecasts?: AiOperationalForecast[]
  continuity?: AiOperationalContinuitySummary
}): AiOperationSimulation => {
  const dependencyImpacts = unique([
    operation.kind === 'CHECKLIST_DEPENDENCY' ? 'Checklist dependency graph would be validated for cycles and cross-workspace links.' : '',
    operation.kind === 'CHECKLIST_STATUS' ? 'Checklist status gates may inspect proof, location, resource, and prerequisite readiness.' : '',
    ...(forecasts ?? []).filter((forecast) => forecast.kind === 'READINESS_BLOCKER' || forecast.kind === 'MISSING_STEP').map((forecast) => forecast.summary),
  ])
  const blockedOperations = intent.risk === 'RESTRICTED'
    ? ['Restricted operations cannot proceed from AI Magic or TourGuide execution surfaces.']
    : operation.requiresReview
      ? ['Persistent mutation is blocked until a proposal is approved.']
      : []
  const requirements = governanceRequirements(operation, intent)

  return {
    summary: intelligencePreview?.summary ?? (operation.requiresReview
      ? 'A trusted proposal would be created for review before any mutation executes.'
      : 'Line can answer, preview, or guide without changing persistent workspace data.'),
    affectedRecords: operation.targetRefs,
    risks: intent.risk === 'LOW' ? [] : [{ level: intent.risk, message: intent.explanation }],
    expectedOutcome: intent.nextStep,
    estimatedAffectedRecordCount: operation.targetRefs.length,
    dependencyImpacts,
    blockedOperations,
    governanceRequirements: requirements,
    rollbackAvailability: rollbackAvailability(operation),
    integrationTouchpoints: integrationTouchpoints(operation),
    riskLevel: intent.risk,
    ...(intelligencePreview ? { intelligencePreview } : {}),
    ...(forecasts?.length ? { forecasts } : {}),
    ...(continuity ? { continuity } : {}),
  }
}
